import { forwardRef } from 'react';
import PaginationButtons from './PaginationButtons';
import PerPagePills from './PerPagePills';
import TextResult from './TextResult';

export type KoobPaginationProps = {
  page: number;
  setPage: (page: number) => void;
  totalCount: number | string;
  totalPages: number | string;
  perPage: number;
  setPerPage: (perPage: number) => void;
  /** `sm` renders the compact map-view layout. */
  size?: 'sm' | 'md';
  block?: boolean;
  /** Drops the standalone card styling (border, radius, background) for
   *  rendering inside another card, e.g. KoobTable's footerBar. */
  embedded?: boolean;
};

const KoobPagination = forwardRef<HTMLDivElement, KoobPaginationProps>(
  (
    {
      page,
      setPage,
      totalCount,
      totalPages,
      perPage,
      setPerPage,
      size = 'md',
      block = false,
      embedded = false,
    },
    ref,
  ) => {
    const compact = size === 'sm';
    const isBlock = compact || block;

    const handleSetPage = (value: number) => {
      setPage(parseInt(String(value)));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSetPerPage = (value: number) => {
      // setPage first: when both write URL search params from the same
      // render, the later setPerPage write (which also resets `page`)
      // must win or it would be clobbered by this one.
      setPage(1);
      setPerPage(parseInt(String(value)));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const total = parseInt(String(totalPages));
    // One page needs no page controls.
    const buttons =
      total > 1 ? (
        <PaginationButtons
          page={page}
          total={total}
          size={size}
          compact={compact}
          onPageChange={handleSetPage}
        />
      ) : null;

    if (!Number(totalCount) || !Number(totalPages)) {
      return null;
    }

    if (embedded) {
      return (
        <div
          ref={ref}
          className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-2 border-t border-solid border-border bg-muted/30"
        >
          <TextResult
            page={page}
            perPage={perPage}
            totalCount={totalCount}
            size={size}
          />
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <PerPagePills
              perPage={perPage}
              size={size}
              onSelect={handleSetPerPage}
            />
            {buttons}
          </div>
        </div>
      );
    }

    if (isBlock) {
      return (
        <div
          ref={ref}
          className="flex flex-col gap-3 w-full p-3 border rounded-md bg-white"
        >
          <div
            className={`flex flex-wrap items-center justify-between ${
              compact ? 'gap-2' : 'gap-4'
            }`}
          >
            <TextResult
              page={page}
              perPage={perPage}
              totalCount={totalCount}
              size={size}
            />
            <PerPagePills
              perPage={perPage}
              size={size}
              onSelect={handleSetPerPage}
            />
          </div>
          {buttons && <div className="flex justify-center">{buttons}</div>}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className="flex flex-row items-center justify-center gap-4 flex-wrap py-3 border rounded-md bg-white mt-2"
      >
        <TextResult
          page={page}
          perPage={perPage}
          totalCount={totalCount}
          size={size}
        />
        <div className="w-px h-5 bg-gray-300 shrink-0" />
        <PerPagePills
          perPage={perPage}
          size={size}
          onSelect={handleSetPerPage}
        />
        {buttons && <div className="w-px h-5 bg-gray-300 shrink-0" />}
        {buttons}
      </div>
    );
  },
);

KoobPagination.displayName = 'KoobPagination';

export default KoobPagination;
